import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import type { PersonalTask } from '@/mocks/calendarEntries';

interface TaskModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (task: Omit<PersonalTask, 'id' | 'completed' | 'color' | 'type'>) => void;
  selectedDate: string;
  existingTask?: PersonalTask | null;
}

export default function TaskModal({ open, onClose, onSave, selectedDate, existingTask }: TaskModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(existingTask?.title ?? '');
  const [dueTime, setDueTime] = useState(existingTask?.dueTime ?? '');
  const [description, setDescription] = useState(existingTask?.description ?? '');

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({ title: title.trim(), date: selectedDate, dueTime: dueTime || undefined, description: description.trim() || undefined });
    setTitle('');
    setDueTime('');
    setDescription('');
    onClose();
  };

  const formattedDate = new Date(selectedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={existingTask ? 'Edit Task' : t('dashboard.addNewTask')}
      subtitle={formattedDate}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSave} disabled={!title.trim()}>{existingTask ? t('common.save') : t('common.add')}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">{t('dashboard.taskTitle')} <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('dashboard.taskTitlePlaceholder')}
            className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg outline-none focus:border-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-400"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">{t('dashboard.dueTime')}</label>
          <input
            type="time"
            value={dueTime}
            onChange={(e) => setDueTime(e.target.value)}
            className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg outline-none focus:border-primary-400 transition-colors text-foreground-900"
          />
        </div>
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">{t('dashboard.taskDescription')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('dashboard.taskDescriptionPlaceholder')}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg outline-none focus:border-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-400 resize-none"
          />
          <p className="text-xs text-foreground-400 mt-1 text-right">{description.length}/500</p>
        </div>
      </div>
    </Modal>
  );
}